import { z } from "zod";

// CSS values frequently get set to `null` in brand configs when a property
// should fall back to default (e.g. transparent backgrounds, no border).
// Coerce null → undefined at parse time so the inferred type stays
// `string | undefined` (compatible with React's CSSProperties) and
// configs don't have to guess between "" / null / omitted.
const css = z.preprocess(
  (v) => (v === null ? undefined : v),
  z.string().optional(),
);

// ─── Shared style primitives ────────────────────────────────────────────────

const textStyle = z.object({
  fontWeight: css,
  fontSize: css,
  fontColor: css,
  textAlign: css,
  textTransform: css,
  letterSpacing: css,
  lineHeight: css,
});

const buttonStyle = z.object({
  backgroundColor: css,
  fontColor: css,
  fontWeight: css,
  fontSize: css,
  borderRadius: css,
  borderColor: css,
  borderWidth: css,
  textTransform: css,
  textAlign: css,
  textDecoration: css,
  letterSpacing: css,
  lineHeight: css,
  outlineColor: css,
  outlineWidth: css,
  outlineOffset: css,
});

const ctaStyles = z.object({
  unfocused: buttonStyle.extend({
    backgroundOpacity: z.string().optional(),
    fontOpacity: z.string().optional(),
    borderTop: z.string().optional(),
    borderBottom: z.string().optional(),
    borderLeft: z.string().optional(),
    borderRight: z.string().optional(),
  }),
  focused: buttonStyle,
  hover: buttonStyle.optional(),
  focus: buttonStyle.partial().optional(),
});

// ─── Route type ─────────────────────────────────────────────────────────────

// "none" mirrors dev's legacy `config.ux.route === "none"` (footwear flows
// that skip the question/vision routing and go straight to BREASTS).
const routeType = z.enum(["question", "vision", "all", "none"]);

// ─── Privacy policy ─────────────────────────────────────────────────────────

const privacyPolicyConfig = z.object({
  consentScreen: z.boolean(),
  hideConsentTitle: z.boolean().optional(),
  url: z.string().optional(),
  urls: z.record(z.union([z.string(), z.record(z.string())])).optional(),
});

// ─── UX configs ─────────────────────────────────────────────────────────────

const inputUXConfig = z
  .object({
    // Whether to call scrollIntoView on focus on mobile. Lacoste opts out
    // because it has its own touchstart-based focus handler.
    scrollIntoViewOnFocusMobile: z.boolean().optional(),
    // Lacoste-style: attach touchstart listener to focus inputs directly
    // (instead of preventing default touchstart on inputs).
    useFocusOnTouchstartMobile: z.boolean().optional(),
    // Hide the placeholder text (Givenchy shows label only).
    hidePlaceholder: z.boolean().optional(),
    // Hide the error icon next to inputs (Givenchy, TDF show no icon).
    hideErrorIcon: z.boolean().optional(),
    // Whether typing an error turns the text red (Intersport keeps black).
    errorTextColor: z.boolean().optional(),
  })
  .optional();

const commonUXConfig = z.object({
  privacyPolicy: privacyPolicyConfig.optional().nullable().default(null),
  showSimilarProducts: z.boolean().optional(),
  forceDesktopLayout: z.boolean().optional(),
  fitFeedbacks: z.boolean().optional(),
  lockSizeCountry: z.string().optional(),
  input: inputUXConfig,
  // TDF-style: on mobile non-footwear, SET the resolvedStep to
  // INTRO_MOBILE but let downstream rules (footwear redirect, mock
  // outOfRange→ERROR, mock qr_code→QR_CODE, etc.) still apply.
  startWithIntroMobile: z.boolean().optional(),
  // Velour-style: when on mobile and the firstStep would be GENDER,
  // EARLY-RETURN INTRO_MOBILE before any other rules run. Strictly
  // stronger than startWithIntroMobile.
  forceIntroMobileOnFirstStep: z.boolean().optional(),
  // Render the gender block BEFORE the height/weight/age user-info
  // inputs on the intro screen (dev's `config.ux.genderBeforeUserInfo`).
  genderBeforeUserInfo: z.boolean().optional(),
  // Insert an extra SLEEVES step after CUISSES instead of submitting
  // (Hast uses long-sleeve measurements).
  includeSleevesStep: z.boolean().optional(),
  // Hide the per-step description text on lingerie questions
  // (Marjolaine opted out).
  hideLingerieStepDescription: z.boolean().optional(),
  // Country → lang fallback for language initialization. Yuxus uses
  // [{ country: "CL", lang: "es" }] so that the Chilean market opens
  // in Spanish even when no ?lang= URL param is set.
  languageOverrides: z
    .array(
      z.object({
        country: z.string(),
        lang: z.string(),
      }),
    )
    .optional(),
});

const flowUXConfig = z.object({
  gender: z.string().nullable(),
  // Where the drawer reads variant stock from (dev's `config.ux.stockSource`):
  //  - "page"     (default) scrape sizes/stock from the merchant page
  //  - "endpoint" fetch live stock from the backend /stocks endpoint
  //    (e.g. Flow / Sessun). When "endpoint", the page-scrape must NOT
  //    override the /stocks data.
  stockSource: z.enum(["page", "endpoint"]).optional(),
});

const clothingUXConfig = flowUXConfig.extend({
  route: routeType,
  skipBreastsStep: z.boolean().optional(),
});

const shoeUXConfig = flowUXConfig.extend({
  route: routeType,
  skipQuestions: z.boolean().optional(),
});

// ─── Common UI ──────────────────────────────────────────────────────────────

const inputFieldTitleStyle = z.object({
  fontWeight: css,
  fontSize: css,
  fontColor: css,
  textTransform: css,
  textAlign: css,
  letterSpacing: css,
  fontStyle: css,
  fontFamily: css,
});

const inputFieldValueState = z.object({
  backgroundColor: css,
  fontWeight: css,
  fontSize: css,
  fontColor: css,
  textAlign: css,
  letterSpacing: css,
});

const inputFieldValueConfig = z.object({
  empty: inputFieldValueState,
  filled: inputFieldValueState,
  borderRadius: css,
  mobileBorderRadius: css,
  borderColor: css,
  padding: css,
  borderBottom: css,
  desktopWidthOffset: css,
  marginBottom: css,
});

const unitsConfig = z.object({
  fontWeight: css,
  fontSize: css,
  top: css,
  right: css,
  activeColor: css,
  inactiveColor: css,
  inactiveFontWeight: css,
  toggle: z.boolean().optional(),
  fontFamily: css,
  textTransform: css,
  letterSpacing: css,
  borderRadius: css,
  border: css,
  padding: css,
  activeBackground: css,
  inactiveBackground: css,
});

const genderCTAConfig = z.object({
  unfocused: buttonStyle,
  focused: buttonStyle,
  // Force textTransform: "uppercase" on mobile regardless of the per-state
  // textTransform (Lacoste shoe-gender mobile screen).
  uppercaseOnMobile: z.boolean().optional(),
});

const sizeSelectorState = z.object({
  backgroundColor: css,
  fontWeight: css,
  fontSize: css,
  fontColor: css,
  textTransform: css,
  borderRadius: css,
  borderColor: css,
  borderWidth: css,
  letterSpacing: css,
  textDecoration: css,
});

const resultScreenUI = z.object({
  generateResult: z.boolean().optional(),
  recommendedSize: textStyle,
  subtitles: textStyle,
  sizeSelector: z.object({
    unfocused: sizeSelectorState,
    focused: sizeSelectorState,
    unavailable: sizeSelectorState.optional(),
  }),
  Description: textStyle.extend({ fontTransform: z.string().optional() }),
  similarProducts: z.object({
    title: textStyle,
    productTitle: textStyle.optional(),
    productPrice: textStyle.optional(),
    lineHeight: css,
  }),
  restartCTA: buttonStyle,
  unavailable_size_text: textStyle,
});

const footerStyle = z.object({
  fontSize: css,
  fontColor: css,
  fontWeight: css,
  textTransform: css,
  letterSpacing: css,
});

const titlesBlock = z.object({
  textAlign: css,
  fontWeight: css,
  textTransform: css,
  color: css,
  fontSize: css,
  fontFamily: css,
  letterSpacing: css,
});

const allUI = z.object({
  font: css,
  // Optional fine-tuning when loading the main font from Google Fonts:
  // Simone Pérèle forces a single 400 weight + a reload to dodge a
  // cached variant.
  fontOptions: z
    .object({
      weights: z.array(z.number()).optional(),
      forceReload: z.boolean().optional(),
    })
    .optional(),
  // Extra custom fonts to preload alongside the main font (Project X
  // Paris uses "Akuratt" as `font` but also needs "Gravity" available).
  additionalCustomFonts: z.array(z.string()).optional(),
  titles: titlesBlock,
  subtitles: titlesBlock,
  cta: ctaStyles,
  footer: footerStyle,
  desktopPopupWidth: css,
  // Background color of the drawer popup (dev's
  // `config.ui.all.drawerBackgroundColor`; SRP / Muscari "#F7F7F7").
  drawerBackgroundColor: css,
  // Privacy-policy text styles rendered on the consent screen (dev's
  // `config.ui.all.Politicy`). Free-form style record — shape is
  // brand-defined CSS, so kept permissive.
  Politicy: z.record(z.unknown()).optional(),
});

const scanCTAStyle = z.object({
  borderWidth: css,
  borderColor: css,
  borderRadius: css,
  fontSize: css,
  fontWeight: css,
  fontColor: css,
  textTransform: css,
  fontStyle: css,
  letterSpacing: css,
  states: z
    .object({
      default: buttonStyle.partial().optional(),
      hover: buttonStyle.partial().optional(),
      focus: buttonStyle.partial().optional(),
      disabled: buttonStyle.partial().optional(),
    })
    .optional(),
});

// ─── Clothing UI ────────────────────────────────────────────────────────────

const morphoStepperConfig = z.object({
  stepNumber: z.string(),
  stepTotal: z.string(),
  focused: z.object({ color: z.string() }),
  unfocused: z.object({ color: z.string() }),
});

const clothingUI = z.object({
  welcome: z.object({
    input_fields: z.object({
      title: inputFieldTitleStyle,
      value: inputFieldValueConfig,
    }),
    units: unitsConfig,
  }),
  morpho: z.object({
    morphoCTA: z.object({
      unfocused: buttonStyle,
      focused: buttonStyle,
    }),
    stepper: morphoStepperConfig,
  }),
  route: z.object({
    routeCTA: z.object({
      borderColor: css,
      borderWidth: css,
      borderRadius: css,
      fontColor: css,
    }),
    deviceSelect: z
      .object({
        default: buttonStyle,
        hover: buttonStyle,
      })
      .optional(),
  }),
  bodyScan: z.object({
    qrcode: z.object({ backgroundColor: css }),
  }),
  questions: z.object({
    scanCTA: scanCTAStyle,
  }),
});

// ─── Lingerie UI ────────────────────────────────────────────────────────────

const sizeSystemState = z.object({
  backgroundColor: css,
  borderColor: css,
  borderWidth: css,
  borderRadius: css,
  fontWeight: css,
  fontSize: css,
  fontColor: css,
  textTransform: css,
  textAlign: css,
  letterSpacing: css,
});

const femaleBraScreenUI = z.object({
  sizeSystem: z.object({
    closed: sizeSystemState,
    open: sizeSystemState,
  }),
  sizeSelector: z.object({
    unfocused: buttonStyle,
    focused: buttonStyle,
  }),
  skip: z.object({
    fontColor: css,
    fontWeight: css,
    fontStyle: css,
    fontSize: css,
    textTransform: css,
    letterSpacing: css,
  }),
});

const lingerieQuestionnaireUI = z
  .object({
    subtitles: z.object({ fontSize: css }).optional(),
    questionCTA: z
      .object({
        focused: buttonStyle,
        unfocused: buttonStyle,
      })
      .optional(),
    stepper: z
      .object({
        type: z.string(),
        activeColor: css,
        inactiveColor: css,
        showLabel: z.boolean().optional(),
        height: css,
      })
      .optional(),
    illustrations: z.record(z.record(z.string())).optional(),
    skipLink: z
      .object({
        fontColor: css,
        fontSize: css,
        fontWeight: css,
        fontStyle: css,
        visible: z.boolean().optional(),
      })
      .optional(),
    result: z.record(z.unknown()).optional(),
    lingerieQuestions: z.array(z.string()).optional(),
    questionnaireMode: z.string().optional(),
    reverseOptionOrder: z.array(z.string()).optional(),
  })
  .optional();

// ─── Silhouettes (body-question image overrides) ────────────────────────────

const silhouetteSet = z.object({
  "1": z.string().optional(),
  "2": z.string().optional(),
  "3": z.string().optional(),
});
const genderedSilhouettes = z.object({
  female: silhouetteSet.optional(),
  male: silhouetteSet.optional(),
});
const silhouettesUISchema = z.object({
  belly: genderedSilhouettes.optional(),
  torso: genderedSilhouettes.optional(),
  cuisses: genderedSilhouettes.optional(),
});

// ─── Full brand config schema ───────────────────────────────────────────────

export const brandConfigSchema = z.object({
  common: z.object({
    ux: commonUXConfig,
    ui: z.object({
      genderCTA: genderCTAConfig,
      result: resultScreenUI,
      all: allUI,
      // Per-brand silhouette image overrides for the body-question
      // steps (Belly / Torse / Cuisses). Consumed by StepBelly /
      // StepTorse via the `silhouettesUI` export in stylesLoader.
      silhouettes: silhouettesUISchema.optional(),
      input: z
        .object({
          // Mobile padding override (e.g. Givenchy "12px 20px 12px 10px",
          // Victoria Beckham "6.5px 15px"); default falls back to
          // input_fields.value.padding from welcome.
          paddingMobile: css,
          // Border style override (Kenzo: "none", everyone else "solid").
          borderStyle: css,
          // Border width override (Blackstore: "2px", Kenzo: "0px").
          borderWidth: css,
          // Use the compact mobile input width (Givenchy + VB):
          // "calc(100% - 32px)" instead of "calc(100% - 42px)", and
          // borderLeftWidth = 0 on the "inches" input.
          compactWidth: z.boolean().optional(),
          // On desktop, the "feet" / "inches" inputs get width 100%
          // instead of the standard "calc(100% - <offset>)" (Blackstore).
          desktopFeetInchesFullWidth: z.boolean().optional(),
          // On desktop, every input gets maxWidth "calc(100% - 20px)"
          // (Blackstore — squeezes the input slightly).
          desktopMaxWidthMinus20: z.boolean().optional(),
          // Fall into the "calc(100% - 20px)" maxWidth branch
          // unconditionally on desktop (alongside the legacy
          // inputStyleSelector.borderBottom === "none" trigger). SRP, VB
          // — both want their desktop inputs squeezed.
          maxWidthMinus20: z.boolean().optional(),
          // On mobile, SKIP the compact calcWidthMobile() path and
          // fall through to the maxWidth-minus-20 / borderBottom branch
          // (SRP — only brand that wants the squeezed bounds on mobile).
          mobileSkipCompactWidth: z.boolean().optional(),
          // Error-icon right offset on the "age" input — Givenchy
          // uses a tighter 30px instead of the default 35px desktop.
          ageErrorIconRightCompact: z.boolean().optional(),
        })
        .optional(),
      measuredBy: z
        .object({
          // Hide the "Measured by Kleep" footer block. Galore opts out
          // entirely.
          hidden: z.boolean().optional(),
          // Hide on the very first intro/gender screen (Nina Ricci).
          hideOnFirstScreen: z.boolean().optional(),
          // Optional brand logo rendered above the footer on non-INTRO
          // desktop steps (LTDC: "assets/brands/logo-LTDS.png").
          brandLogo: z
            .object({
              src: z.string(),
              alt: z.string(),
              width: z.string(),
            })
            .optional(),
          // Privacy policy block overrides
          policy: z
            .object({
              // Always show regardless of step (Givenchy).
              alwaysShow: z.boolean().optional(),
              // Show on the TDF mobile INTRO_MOBILE step (TDF).
              showOnIntroMobile: z.boolean().optional(),
              color: css,
              alignment: css,
              bottomDesktop: css,
              bottomMobile: css,
              marginTopMobile: css,
            })
            .optional(),
          // Measured-by block overrides
          block: z
            .object({
              color: css,
              fontSize: css,
              fontWeight: css,
              positionAlwaysRelative: z.boolean().optional(),
              bottomDesktop: css,
              alignment: css,
              leftPercentMobile: css,
              transformMobile: css,
              extraClassName: css,
            })
            .optional(),
          // Wrapper overrides
          wrapper: z
            .object({
              positionDesktop: css,
              leftDesktop: css,
              mobileMarginTopAuto: z.boolean().optional(),
            })
            .optional(),
        })
        .optional(),
      genderButton: z
        .object({
          // Padding override (Peserico: "12px 16px"; default: "10px 20px").
          padding: css,
          // Min-height override (TDF: "28px", Givenchy: "40px";
          // default: "45px").
          minHeight: css,
          // Hover border color override (Intersport, Blackstore: "#A1A1A1";
          // default: keep base borderColor).
          hoverBorderColor: css,
        })
        .optional(),
      // Layout overrides for the gender selector row (GenderBlock).
      genderBlock: z
        .object({
          // Render the "male" gender button first (Velour Garments).
          maleFirst: z.boolean().optional(),
          // Constrain the gender block to 50% width on desktop
          // (Givenchy, Marcy Paris).
          widthDesktopHalf: z.boolean().optional(),
          // Don't auto-advance on mobile after gender selection
          // (Givenchy keeps the user on the gender step until they
          // tap the continue CTA).
          noAutoAdvanceOnMobile: z.boolean().optional(),
          // Justify-content override for the desktop layout (Givenchy
          // "space-between").
          desktopAlignment: css,
          // Center the gender block horizontally (Blackstore).
          centerAlign: z.boolean().optional(),
          // Gap between the gender buttons (Givenchy "10px"; default
          // "8px" or via CSS).
          gap: css,
          // Bottom margin on the gender block on mobile (Givenchy
          // "20px").
          marginBottomMobile: css,
          // Skip the large-height-relative marginTop rule on the
          // gender block (Sporty & Rich).
          skipLargeHeightMarginRule: z.boolean().optional(),
          // Render gender buttons in a row instead of the default
          // column on mobile (TDF).
          mobileFlexDirectionRow: z.boolean().optional(),
        })
        .optional(),
      // Per-brand padding size on the bra-size selector + the shoe
      // size component. Default = "small" (10px breasts, 10px shoes).
      // "medium" = 13px breasts / 15px shoes (the legacy
      // MEDIUM_PADDING_BRANDS list — ~52 brands). "12px" = literal
      // 12px (Z&V + Lacoste — bespoke compact size).
      sizeSelectorPaddingSize: z
        .enum(["small", "medium", "12px"])
        .optional(),
      // Compact-input-group brands (Etam, Maison 123) share layout
      // traits: zero marginTop on descriptions around the welcome
      // inputs, tighter step header offsets, etc. (Sans Complexe joins
      // them ONLY for the popup-width breakpoint in usePopupStyles —
      // see isCompactInputGroup there, which keeps Sans Complexe in
      // its inline brand list to avoid a behavior change.)
      compactInputGroup: z.boolean().optional(),
      // Use the smaller "customSize" variant for the close/back arrow
      // button (Blackstore, Intersport, TDF use a more compact arrow).
      smallCloseButton: z.boolean().optional(),
      // Use shoe-specific error copy keys when on a footwear flow
      // (Intersport and Blackstore branded error messages).
      useShoeSpecificErrors: z.boolean().optional(),
      // Victoria-Beckham-style unit options (bordered tab look; VB and
      // Velour Garments override defaults differently for padding).
      unitOptions: z
        .object({
          // When true, units render in a bordered "tab" form with
          // alternating active/inactive backgrounds (VB, Velour).
          bordered: z.boolean().optional(),
          // Padding override (VB "10px", Velour "5px"; default "none").
          padding: css,
          // Hide the unit-options toggle entirely when the URL
          // ?countryCode is "EU" (Devernois opts out of cm/in switch
          // on the European market — EU sizes are fixed).
          hideOnMarketEU: z.boolean().optional(),
        })
        .optional(),
      // Per-brand overrides for the GeneratedSizes block on the result
      // page (Lacoste shrinks the width and trims padding).
      generatedSizes: z
        .object({
          widthDesktop: css,
          paddingTopDesktop: css,
          paddingBottomMobile: css,
        })
        .optional(),
      // Right offset for the silhouette image on the size selector
      // (Lacoste pulls it 30px to the right on mobile).
      sizeSelectorImageRightMobile: css,
      description: z
        .object({
          // Hide the description block entirely (VB, Velour Garments).
          hidden: z.boolean().optional(),
          // Render a second description span (titlesStyles-based)
          // beneath the main one. Givenchy + Simone Pérèle use this.
          showSecondary: z.boolean().optional(),
          // Show the main description on the gender step on mobile
          // (default is to hide it on mobile gender). Simone Pérèle,
          // Givenchy, TDF, Etam, Maison 123, Boggi Milano opt in.
          showOnMobileGender: z.boolean().optional(),
          // Show a small "gender.title" subtext on the gender step on
          // mobile (SRP only).
          showGenderTitleOnMobile: z.boolean().optional(),
          // CSS line-height override for the description span
          // (Givenchy uses 20px).
          lineHeight: css,
        })
        .optional(),
      // Overrides for the custom-design "size selectors" component used
      // on the result page when resultLayout.useCustomDesign is true.
      // Currently consumed by the Givenchy + Victoria Beckham layouts;
      // VB sticks to defaults, Givenchy sets all of these.
      customSizeSelectors: z
        .object({
          gap: css,
          marginYDesktop: css,
          feedbackMarginTop: css,
          mobileLabelTopOffset: css,
          selectedFeedbackUnderline: z.boolean().optional(),
        })
        .optional(),
      scanCTA: z
        .object({
          hideOnImperial: z.boolean().optional(),
          maxHeight: css,
          maxHeightMobile: css,
          padding: css,
          contentPadding: css,
          marginBottomMobile: css,
          contentGap: css,
          contentGapMobile: css,
          iconType: z.enum(["camera", "settings"]).optional(),
          // Small icon on every device (TDF).
          iconSmall: z.boolean().optional(),
          // Small icon only on mobile (Blackstore).
          iconSmallMobile: z.boolean().optional(),
          iconWrapperMinWidthMobile: css,
          textSizeMobile: css,
          // Mobile fontSize reduction in px (Blackstore reduces by 4
          // instead of the default 1).
          mobileFontSizeReduction: z.number().optional(),
          mobileWhiteSpace: css,
          className: css,
          skipCapitalizeOnGerman: z.boolean().optional(),
        })
        .optional(),
      title: z
        .object({
          // Hide the step title block entirely (VB renders its own).
          hidden: z.boolean().optional(),
          // Hide on mobile entirely (Velour Garments).
          hiddenOnMobile: z.boolean().optional(),
          // Show the title on the QR_CODE step (TDF, Givenchy opt in;
          // default is to hide on QR_CODE).
          showOnQRCode: z.boolean().optional(),
          // Hide on TDF-style intro/device-select steps (TDF replaces
          // them with the back arrow text).
          hideOnIntroAndDeviceSelect: z.boolean().optional(),
          // Render a back-arrow icon next to the title (TDF).
          showBackArrow: z.boolean().optional(),
          // Min-height override (TDF "28px"; default "25px").
          minHeight: css,
          // Don't capitalize the description text (Givenchy keeps the
          // raw translation casing).
          preserveCase: z.boolean().optional(),
          // CSS class override (PXP "h2-pxp").
          className: css,
          // Force text-align center on the RESULT step (Lacoste).
          centerAlignOnResult: z.boolean().optional(),
          // Mobile margin overrides (Givenchy 30px top, SRP 0 on RESULT).
          marginTopMobile: css,
          // Desktop margin top (Givenchy 0).
          marginTopDesktop: css,
          // Mobile margin top when on the RESULT step (SRP 0).
          marginTopMobileResult: css,
        })
        .optional(),
      descriptionResults: z
        .object({
          // Lacoste hides the whole block on the lingerie flow.
          hideOnLingerie: z.boolean().optional(),
          // Wrapper margin (Givenchy + Boggi: "30px 0", TDF "20px 0",
          // SRP "32px 0"; default "15px 0").
          margin: css,
          // Wrapper padding (Peserico "12px 16px").
          padding: css,
          // Desktop-only paddingBottom (Gerard Darel "10px").
          paddingBottomDesktop: css,
          // Skip the border-bottom even when similarProducts are shown
          // (Givenchy / SRP keep the unavailable state borderless).
          hideBorderBottom: z.boolean().optional(),
          // Description text font-size override (SRP "14px").
          fontSize: css,
          // Lower-case the first letter of the description (Molli).
          lowercaseFirstLetter: z.boolean().optional(),
          // Style for the unavailable variant (SRP)
          unavailable: z
            .object({
              backgroundColor: css,
              borderRadius: css,
              padding: css,
              textColor: css,
              showInfoIcon: z.boolean().optional(),
            })
            .optional(),
        })
        .optional(),
      shoeQuestion: z
        .object({
          // Hover border color for the shoe-question option buttons
          // (Intersport uses #A1A1A1; default keeps the base borderColor).
          hoverBorderColor: css,
        })
        .optional(),
      // Overrides for the StepBreasts component (the bra-band + bra-cup
      // selection grid on the lingerie flow).
      stepBreasts: z
        .object({
          // ── Layout / sizing ──────────────────────────────────────
          // Use the bespoke StyledSelector for the country dropdown
          // instead of the default BaseSizeSelector (SRP).
          useStyledCountrySelector: z.boolean().optional(),
          // Min height/width on each size button on desktop
          // (Intersport + Blackstore "48px"; default = none).
          sizeButtonDesktopMinSize: css,
          // Apply the compact-input-group bra grid (min-height 40px,
          // flex 20% rows). compactInputGroup brands opt in.
          sizeButtonUseCompactGroup: z.boolean().optional(),
          // Apply the Sans-Complexe-style 4-column grid (min-height
          // 36px, flex 25% rows).
          sizeButtonUseFourColGroup: z.boolean().optional(),
          // Render fixed-size square buttons (42×42 with 0 border
          // radius). Simone Pérèle layout.
          sizeButtonFixedSize: css,
          // Size buttons render with width "auto" + a custom min-width
          // (Simone Pérèle "40px").
          sizeButtonAutoWidth: z.boolean().optional(),
          sizeButtonMinWidthOverride: css,
          // Gap between buttons in the sizes grid (Simone Pérèle +
          // compactInputGroup "4px", Sans Complexe "5px"; default
          // "8px" from CSS).
          sizesGridGap: css,
          // Filter out specific band sizes from the FR country option
          // (Sans Complexe excludes 75 and 80 on FR).
          hideBandSizes75And80OnFR: z.boolean().optional(),
          // Filter out the AA and A cup sizes (Sans Complexe).
          hideCupSizesAAndAA: z.boolean().optional(),
          // ── Description ──────────────────────────────────────────
          // Hide the description paragraph entirely (Victoria Beckham).
          hideDescription: z.boolean().optional(),
          // Mobile description marginTop default ("0" if SRP-style
          // override applied; else "0"). Default is "0" on mobile
          // EXCEPT for brands that set this to "10px". SRP sets the
          // marginTop on mobile.
          descriptionMarginTopMobileOverride: css,
          // Force display: block on the description (Simone Pérèle).
          descriptionDisplayBlock: z.boolean().optional(),
          // ── Title (band / cup labels) ────────────────────────────
          // Use the modal titleStyles.color for the section labels
          // instead of welcomeUI.input_fields.title.fontColor (Givenchy).
          titleUseModalTitleColor: z.boolean().optional(),
          // Override the section-label fontSize with descriptionStyles
          // .fontSize (Givenchy uses the smaller description text size).
          titleUseDescriptionFontSize: z.boolean().optional(),
          // Inject the selected band size / cup size as the {value}
          // arg into the t("size") / t("cup") translation (Givenchy
          // renders "Size: 90B" instead of "Size").
          sizeTitleIncludesSelectedValue: z.boolean().optional(),
          cupTitleIncludesSelectedValue: z.boolean().optional(),
          // ── Section labels margin ────────────────────────────────
          // Size title (band) marginBottom (Simone Pérèle "10px").
          sizeTitleMarginBottom: css,
          // Cup title margin overrides per device.
          cupTitleMarginTopMobile: css,
          cupTitleMarginBottomDesktop: css,
          // ── Table layout ─────────────────────────────────────────
          // Table marginBottom on desktop (Givenchy "10px").
          tableMarginBottomDesktop: css,
          // ── CTA ──────────────────────────────────────────────────
          // Continue CTA margin overrides on desktop (VB "60px" both
          // top + bottom; default 0 bottom / "30px" top).
          ctaMarginBottomDesktop: css,
          ctaMarginTopDesktop: css,
          // CTA padding (Peserico "12px 16px", VB "7px"; default "16px").
          ctaPadding: css,
          // CTA height (VB "30px"; default "auto").
          ctaHeight: css,
          // CTA maxHeight (TDF "28px", Givenchy "40px"; default
          // "fit-content").
          ctaMaxHeight: css,
          // CTA pinning on desktop (Givenchy desktop pins the CTA to
          // a fixed bottom offset, collapsing to relative on small
          // heights).
          ctaPinDesktop: z.boolean().optional(),
          ctaPinDesktopBottom: css,
          ctaPinDesktopWidth: css,
          // Threshold (in px) for the small-height breakpoint that
          // disables CTA pinning (Givenchy "820"; default "720").
          ctaPinSmallHeightThreshold: z.number().optional(),
          // Render a ChevronRight on the continue CTA (Intersport).
          showChevronOnCTA: z.boolean().optional(),
          // Use the raw t("continue") key on the CTA (skip the
          // capitalize-first-letter pass — Theodore wants lowercase).
          skipContinueCapitalize: z.boolean().optional(),
          // ── Skip button ──────────────────────────────────────────
          // Hide the "skip" button below the CTA (VB + Simone Pérèle —
          // these flows have no skip option).
          hideSkipButton: z.boolean().optional(),
          // Force uppercase on the skip button text (Zadig + Givenchy
          // + Barbara Bui — capitalized skip CTA).
          skipButtonUppercase: z.boolean().optional(),
          // Pin the skip button to a fixed bottom offset on desktop
          // (Givenchy desktop only; collapses on small-height).
          skipButtonPinDesktop: z.boolean().optional(),
          skipButtonPinDesktopBottom: css,
          skipButtonPinDesktopWidth: css,
          // Skip button marginBottom on the small-height desktop path
          // (Givenchy "80px"; default "20px").
          skipButtonMarginBottomSmallHeightDesktop: css,
        })
        .optional(),
      // Overrides for the ShoesComponentSize (the shoe-size table on
      // the lingerie-style sizing flow when shoeQuestion path is on).
      shoesComponentSize: z
        .object({
          // Min height/width on each size button on desktop. Intersport
          // and Blackstore both use "48px" for a chunkier hit area;
          // default = no min constraint.
          desktopMinSize: css,
          // Use a custom size-list source instead of SHOES_BASE_SIZES
          // (Vanessa WU ships its own per-country size grid). Currently
          // only one alternative exists; future clients can register
          // additional ones here.
          alternateSizeListSource: z
            .enum(["vanessa"])
            .optional(),
          // Use the bespoke StyledSelector for the country dropdown
          // instead of the default BaseSizeSelector (SRP — bigger
          // tap targets + matching SRP visual language).
          useStyledCountrySelector: z.boolean().optional(),
          // Render a ChevronRight icon on the continue CTA (Intersport).
          showChevronOnCTA: z.boolean().optional(),
        })
        .optional(),
      lingerieQuestion: z
        .object({
          // Drop the description's marginTop (Etam + Maison 123 sit
          // tighter against the title block).
          descriptionNoMarginTop: z.boolean().optional(),
          // Show the MeasuredBy footer on lingerie question steps
          // (Sans Complexe — other brands hide it on these screens).
          showMeasuredBy: z.boolean().optional(),
          // Skip the description text capitalization (Simone Pérèle
          // wants the description rendered verbatim).
          skipDescriptionCapitalize: z.boolean().optional(),
          // Render the per-option subtitle text (Simone Pérèle reads
          // the `subs.<key>` translation under each option).
          showOptionSubtitle: z.boolean().optional(),
        })
        .optional(),
      backArrow: z
        .object({
          // Icon button `top` offset (SRP 12px, Intersport/Blackstore 5px,
          // default 10px).
          topOffset: css,
          // Mobile `left` offset on the wrapper (Givenchy 5px,
          // default 20px).
          leftMobile: css,
          // Keep showing the back arrow even on RESULT / ERROR /
          // UNAVAILABLE_RESULT screens (Givenchy opts in).
          forceShowOnResultScreens: z.boolean().optional(),
          // Hide the back arrow on the mobile INTRO_MOBILE step
          // (Velour Garments).
          hideOnMobileIntro: z.boolean().optional(),
          // Render the back affordance as a plain "Back" text label on
          // desktop instead of the icon (Givenchy desktop).
          renderAsTextDesktop: z.boolean().optional(),
          // Replace the icon by the current step's name text (TDF).
          renderAsStepName: z.boolean().optional(),
          // Use a brand-specific custom back-arrow icon on mobile
          // instead of the default ArrowBackIcon. Currently the only
          // supported value is "chevron" (a left-pointing chevron used
          // by Givenchy). Default leaves the standard icon.
          customMobileIcon: css,
          // Sporty & Rich uses a smaller-height breakpoint for the
          // wrapper top adjustment: when true, the "medium height" media
          // query triggers the 5px top (vs the default "large height").
          reverseHeightBreakpoint: z.boolean().optional(),
        })
        .optional(),
      // QR code overrides shared between QrcodeDesktop + QrcodeMobile.
      qrcode: z
        .object({
          // Hide the help icon next to the info link (Givenchy, both
          // desktop and mobile QR screens).
          hideHelpIcon: z.boolean().optional(),
          // Force the link/popper text + border color (Givenchy: black).
          linkColor: css,
          // Desktop-only — skip the 50px top margin on the QR (Givenchy).
          noTopMargin: z.boolean().optional(),
          // Desktop-only — popper position offset (Givenchy bumps down).
          popperOffsetTop: css,
          popperOffsetX: z.number().optional(),
          popperOffsetY: z.number().optional(),
          popperFontSize: css,
          // Mobile-only — show a ChevronRight arrow next to the scan
          // CTA label (Intersport).
          showChevronOnCTA: z.boolean().optional(),
          // Desktop-only — outer container minHeight on the QR step
          // (Givenchy desktop "68vh"; default "79vh").
          containerMinHeightDesktop: css,
          // Gap between the title + description block on the QR step
          // (Givenchy "5px"; default "20px").
          textBlockGap: css,
          // Top margin on the title/description block (Givenchy mobile
          // "0px", desktop "15px"; default "25px" both).
          textBlockMarginTopMobile: css,
          textBlockMarginTopDesktop: css,
          // Top margin on the title text inside the block (Givenchy
          // mobile "0", default "10px"; Z&V + Lacoste desktop "10px",
          // default "0").
          titleMarginTopMobile: css,
          titleMarginTopDesktop: css,
          // Override title + description font size on desktop (SRP
          // "18px"; default keeps the inherited title/description size).
          textFontSizeDesktop: css,
          // Override the description whiteSpace on desktop (Intersport
          // "normal"; default "nowrap").
          descriptionWhiteSpaceDesktop: css,
          // On mobile, use descriptionStyles.fontSize for the title row
          // instead of titleStyles.fontSize (Givenchy). Default uses
          // titleStyles on mobile.
          titleUseDescriptionFontSizeOnMobile: z.boolean().optional(),
        })
        .optional(),
      // When false, the photo/questions buttons on the device-select
      // step are NOT reversed on a footwear flow (default true).
      // Intersport and Blackstore opt out — they want the standard
      // questions-first order even on footwear.
      reverseDeviceButtonsOnFootwear: z.boolean().optional(),
      // Overrides for the StepIntroDesktop component (the gender +
      // height/weight/age intro screen on both mobile + desktop).
      stepIntro: z
        .object({
          // Inject the --focus-color / --focus-offset / --focus-width
          // CSS variables on the continue CTA so the brand's CSS can
          // style the focus ring from common.ui.all.cta.focus.* values.
          // Lacoste uses this for an accessibility-compliant focus.
          injectFocusOutlineCSSVars: z.boolean().optional(),
          // Mobile bottom margin on the continue CTA (TDF / SRP "20px",
          // VB "30px"; default "10px"). Defaults preserve legacy.
          ctaMarginBottomMobile: css,
          // Desktop bottom margin on the continue CTA (Givenchy desktop
          // "122px"; default "80px").
          ctaMarginBottomDesktop: css,
          // Top margin on the continue CTA on mobile when either the
          // brand opts in (Givenchy mobile) or compactInputGroup is true.
          // Default 0.
          ctaMarginTopMobile: css,
          // Pin the continue CTA to a fixed bottom offset on desktop
          // (Givenchy desktop). When the viewport is small-height the
          // pin collapses to relative + a small top offset.
          ctaPinDesktop: z.boolean().optional(),
          ctaPinDesktopTopOnSmallHeight: css,
          ctaPinDesktopBottom: css,
          ctaPinDesktopWidth: css,
          // Mobile bottom override on the continue CTA — used by
          // mobile SRP ("22px"). Default falls back to ctaMarginBottomMobile.
          ctaBottomMobile: css,
          // Show the gender block on the INTRO_MOBILE step (Velour
          // renders gender selection on the intro mobile screen).
          showGenderBlockOnIntroMobile: z.boolean().optional(),
          // Always show the gender block regardless of step (TDF —
          // gender label + radio render even when not on the GENDER step).
          showGenderBlockAlwaysOnMobile: z.boolean().optional(),
          // Form top margin on mobile (Givenchy mobile "10px", Velour
          // "40px"; default "30px"). Default 0 on desktop.
          formMarginTopMobile: css,
          // Form top margin on desktop (Velour sets "40px" — the
          // marginTop applies on both devices). Default 0.
          formMarginTopDesktop: css,
          // When true, the form gap collapses to "5px" on the medium-
          // height-relative breakpoint (Petrone, Givenchy). Sporty &
          // Rich inverts via popup.useMediumHeightBreakpoint.
          gapFiveOnMediumHeight: z.boolean().optional(),
          // Gap on the gender block (Givenchy + TDF "5px"; default
          // "20px").
          genderBlockGap: css,
          // Mobile bottom margin on the gender block (Givenchy mobile
          // "0", SRP on GENDER step "20px"; default "0").
          genderBlockMarginBottomMobileOnGender: css,
          // Show the gender label on mobile (TDF, Givenchy — both
          // render the label even on mobile; default hides it).
          showGenderLabelOnMobile: z.boolean().optional(),
          // Margin-top on the gender label (TDF mobile "20px"; TDF
          // desktop "10px"; Givenchy "30px"; default 0).
          genderLabelMarginTopMobile: css,
          genderLabelMarginTopDesktop: css,
          // Render the gender label with the currently-selected
          // gender value substituted into the translation via
          // t("gender.title", { value: <gender> | "Select" }). Default
          // = render the static t("gender.title") with no value.
          genderTitleIncludesSelectedValue: z.boolean().optional(),
          // Render a privacy-policy checkbox + i18n link block after
          // the user info inputs, before the gender block. Nina Ricci
          // currently uses this; the flag is generic.
          showPolicyCheckbox: z.boolean().optional(),
          // Render a ChevronRight on the continue CTA (Intersport).
          showChevronOnCTA: z.boolean().optional(),
          // Show the continue CTA on mobile even when the user is on
          // the GENDER step (Givenchy mobile — other brands auto-advance
          // after gender selection so the CTA stays hidden).
          showCTAOnMobileGender: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the UserInfoBlock (the height/weight/age form
      // inputs rendered on the intro screen).
      userInfoBlock: z
        .object({
          // Keep the inline UnitOptions component on mobile instead of
          // rendering the toggle-group label header (SRP keeps the
          // CM/FEET buttons inside the input row on mobile).
          inlineUnitSwitcherOnMobile: z.boolean().optional(),
          // Hide the imperial unit toggle when the URL ?countryCode is
          // "EU" (Devernois EU surfaces only metric units).
          hideImperialUnitToggleInEU: z.boolean().optional(),
          // Override the StyledToggleButton dimensions (Galore uses a
          // smaller 32x17px toggle with 9px font for a denser layout).
          unitToggleSizeOverride: z
            .object({
              width: z.string(),
              height: z.string(),
              fontSize: z.string(),
              padding: z.string(),
            })
            .optional(),
          // Override the wrapper padding around the toggle group
          // (Galore "2px 6px"; default "1px 5px").
          unitToggleWrapperPadding: css,
          // Top offset applied to the mobile WrapperBlock header
          // (Etam / Maison 123 "8px" — visually centers their compact
          // input row).
          wrapperBlockTopOffsetMobile: css,
          // Desktop width of the feet input field (SRP "calc(100% + 2px)",
          // Blackstore "calc(100% - 5px)", Sporty & Rich "calc(100% - 15px)",
          // Etam / Maison 123 "calc(100% - 10px)"; default "100%").
          feetInputWidthDesktop: css,
          // Gap between the feet and inches inputs (Givenchy + VB +
          // Velour use "0px" both mobile and desktop; default
          // "10px" / "20px").
          feetInchesGap: z
            .object({ mobile: css, desktop: css })
            .optional(),
          // Gap between the year and month inputs on the children flow
          // (VB + Velour "0px"; default "10px" / "20px").
          yearMonthGap: z
            .object({ mobile: css, desktop: css })
            .optional(),
          // Inject the current unit value into the field title via
          // i18n interpolation, e.g. t("height.title", { value }).
          // Givenchy uses this to render "height (cm)".
          injectUnitInTitle: z.boolean().optional(),
          // Render the field placeholder again as a lowercase hint
          // span below the input (Givenchy — accessibility/UX choice
          // showing the expected format inline).
          showInlinePlaceholderHint: z.boolean().optional(),
          // Mobile margin-top on the weight label (Givenchy "15px";
          // default "0px").
          weightLabelMarginTopMobile: css,
          // Desktop margin-top on the weight label (Etam / Maison 123
          // "10px", Givenchy "20px"; default "0px").
          weightLabelMarginTopDesktop: css,
          // Mobile margin-top on the age label (Etam / Maison 123
          // "15px", Givenchy "20px"; default "10px").
          ageLabelMarginTopMobile: css,
          // Desktop margin-top on the age label (Etam / Maison 123
          // "10px", Givenchy "20px"; default "0px").
          ageLabelMarginTopDesktop: css,
          // Margin-bottom on the age label (Givenchy + TDF "5px";
          // default "0").
          ageLabelMarginBottom: css,
          // Margin-bottom on the age label when the device is Android
          // (Sporty & Rich "10px" — extra spacing for android-only
          // line-height drift).
          ageLabelMarginBottomAndroid: css,
          // Bottom offset of the age-error spacer container (Sans
          // Complexe "20px", TDF "5px", Givenchy "0"; default "10px").
          ageErrorContainerBottomOffset: css,
          // Bottom offset of the age-error <span> itself (Etam /
          // Maison 123 "-5px"; default "7px").
          ageErrorSpanBottomOffset: css,
          // Override the desktop error-title className (TDF
          // "baseErrorTitleRelative"; default "baseErrorTitle").
          errorTitleClassNameDesktop: z.string().optional(),
          // Mobile className appended to the error spacer wrapper
          // (TDF "baseErrorTitleMargin"; default "").
          errorTitleClassNameMobile: z.string().optional(),
          // Render an extra "company" text field after the age block
          // (Marcy Paris B2B flow).
          showCompanyField: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the BaseSizeSelector (the bra/shoe size-system
      // dropdown on the lingerie + shoe steps).
      braSizeSelector: z
        .object({
          // Render a compact custom dropdown instead of the MUI
          // <Select>-based default (Givenchy uses this — bespoke
          // CompactDropdownSelector sub-component with custom-styled
          // option list).
          useCompactDropdown: z.boolean().optional(),
          // Force a specific size-list preset: "europe" = EU/IT/FR
          // (Devernois EU); "europeUK" = EU/FR/IT/UK (Sans Complexe
          // adds the UK option).
          sizeListPreset: z.enum(["europe", "europeUK"]).optional(),
          // Only apply sizeListPreset:"europe" when the URL
          // ?countryCode is "EU" (Devernois opts in this way).
          ifMarketEUUseEuropePreset: z.boolean().optional(),
          // Flag-icon span "left" offset inside each MenuItem
          // (Victoria Beckham "12px"; default "8px").
          flagSpanLeft: css,
          // Flag-icon span fontStyle override (VB sets "normal" to
          // override an inherited italic style; default "").
          flagSpanFontStyle: css,
        })
        .optional(),
      // Overrides for the SizeSelectorsComponent (the row of "smaller
      // / good / bigger" size buttons under the recommended size on
      // the apparel result screen).
      sizeSelectorsComponent: z
        .object({
          // Sort the selector buttons by label_rank (smaller → good →
          // bigger) with a bespoke active-index calculation. Sud
          // Express uses this layout for clarity on a 2-button row.
          useLabelRankSortedLayout: z.boolean().optional(),
          // Mobile-only padding override (Sessun "20px 10px", Boggi
          // Milano "0"; default "inherit").
          mobilePadding: css,
          // Mobile-only fontSize override on the label (Boggi Milano
          // "11px"; default uses secondSelectorStyles.fontSize).
          mobileFontSizeOverride: css,
          // Mobile-only margin override on the label (Boggi Milano
          // "0 5px"; default "").
          mobileItemMargin: css,
        })
        .optional(),
      // Overrides for the root DrawerModal component (the modal
      // container that orchestrates every step). All fields generic.
      drawerModal: z
        .object({
          // Always use LANGUAGE_RULES.default regardless of ?lang URL
          // param (Velour Garments — single-language flow).
          forceDefaultLanguageRules: z.boolean().optional(),
          // Derive the country code from the URL subdomain prefix
          // (Lacoste — country-specific subdomain routing).
          useSubdomainForCountry: z.boolean().optional(),
          // Require a non-empty, digit-free "company" field before
          // letting the user continue (Marcy Paris B2B flow).
          requireCompany: z.boolean().optional(),
          // Require the policy checkbox to be ticked before continue
          // (Nina Ricci — also wires the checkbox via stepIntro.showPolicyCheckbox).
          requirePolicyChecked: z.boolean().optional(),
          // Skip the runtime checkStocks() call (Asphalte + SAAJ —
          // both ship their own stock-resolution paths).
          skipStockCheck: z.boolean().optional(),
          // When matching the selected size against productStockData,
          // ALSO accept matches by size.label (Antonelle exposes
          // size.label as the variant identifier; default matches by
          // variant_id / reference only).
          matchVariantByLabel: z.boolean().optional(),
          // Overlay (modal backdrop) background color (Givenchy uses
          // a near-white "rgba(245, 246, 247, 0.8)" instead of the
          // standard semi-transparent black).
          overlayBackgroundColor: css,
          // Outline focus ring color injected as the CSS variable
          // --focus-color on the overlay (Blackstore "#FFDD00",
          // Lacoste "#79BE76"; default "#036ded").
          overlayFocusColor: css,
          // Popup content area background (SRP "#F7F7F7"; default
          // "#FFFFFF").
          contentBackgroundColor: css,
          // Popup content area padding on desktop (Givenchy
          // "100px 40px 0 40px"; default 0).
          contentPaddingDesktop: css,
          // Render the title-guide block in the header (SRP uses the
          // HeaderTitleGuide component to render a contextual header
          // tagline).
          showHeaderTitleGuide: z.boolean().optional(),
          // Render the step-guide block in the header (Victoria
          // Beckham uses HeaderStepGuide to render a step
          // indicator strip).
          showHeaderStepGuide: z.boolean().optional(),
          // Extra CSS class appended to the popup content on the
          // INTRO_MOBILE step (Sans Complexe sets
          // "sans-complexe-intro-mobile" for a brand-specific class).
          introMobileExtraClass: css,
        })
        .optional(),
      // Overrides for the Result.tsx component (the main apparel
      // result screen — shared with the lingerie path; the footwear
      // path is rendered by ShoeResult). Everything is optional;
      // defaults preserve the legacy non-brand-keyed behavior.
      // NOTE: this key is intentionally `resultLayout`, NOT `result`,
      // because the legacy `common.ui.result` block already holds the
      // styling data (resultScreenUI in stylesLoader.ts).
      resultLayout: z
        .object({
          // ── Top-level layout ──────────────────────────────────────
          // Render the "custom" layout branch — skip the standard
          // h1 + SizeSelectorsComponent block (Givenchy, VB).
          useCustomDesign: z.boolean().optional(),
          // Skip the GeneratedSizes / DescriptionResults middle block
          // entirely (Velour Garments, Victoria Beckham).
          skipResultBlock: z.boolean().optional(),
          // Render an empty spacer of the given height in place of the
          // result block (Velour: "40px"). Undefined = no spacer.
          emptyResultBodySpacerHeight: css,
          // Skip the SizeSelectorsComponent inside the apparel result
          // (Loom hides selectors).
          skipSizeSelectors: z.boolean().optional(),
          // ── Recommended size h1 ───────────────────────────────────
          // Extra CSS class (TDF "millerBannerFont").
          recommendedSizeClassName: css,
          // When unavailable, use the standard recommendedSize fontColor
          // instead of unavailable_size_text fontColor (SRP).
          unavailableUseRecommendedColor: z.boolean().optional(),
          // Skip the "40px" mobile fontSize override (Lacoste keeps
          // its full configured size on mobile; default mobile is 40px).
          skipMobileFontSizeReduction: z.boolean().optional(),
          // Font-weight override (TDF 300).
          recommendedSizeFontWeight: z.number().optional(),
          // Desktop marginTop = 0 instead of "54px" (Gerard Darel
          // desktop).
          recommendedSizeMarginTopDesktopZero: z.boolean().optional(),
          // Force marginBottom = 0 regardless of height/device (TDF).
          recommendedSizeMarginBottomZero: z.boolean().optional(),
          // CSS top offset on the recommended-size h1 (TDF "20px").
          recommendedSizeTopOffset: css,
          // ── Size title (h2 under the h1) ──────────────────────────
          // Hide the h2 when selectedVariant === "1" (TDF).
          hideSizeTitleOnVariant1: z.boolean().optional(),
          // Override the h2 color (Boggi Milano "#44883F").
          sizeTitleColor: css,
          // h2 fontWeight (Z&V + Lacoste 700).
          sizeTitleFontWeight: z.number().optional(),
          // Hide the checkmark icon next to the h2 (Z&V + Lacoste).
          hideCheckmarkIcon: z.boolean().optional(),
          // ── Size-selector "unavailable" overrides ─────────────────
          // CSS overrides applied to the size selector when the
          // currently-recommended size is unavailable. SRP sets all
          // four to dim/alpha values so the unavailable size renders
          // as a low-contrast greyed-out tile.
          unavailableSelectorBackgroundColor: css,
          unavailableSelectorTextColor: css,
          unavailableSelectorBorderColor: css,
          // Color applied to the h2 (size title) on the unavailable
          // branch (SRP "rgba(3, 2, 2, 0.32)").
          unavailableSizeTitleColor: css,
          // Render the CheckIconUnavailable SVG (a dimmed checkmark
          // variant) next to the size title instead of the default
          // CheckCircleIcon. Used together with the dimmed colors
          // above to flag the recommended-but-unavailable case.
          useDimmedCheckmarkIcon: z.boolean().optional(),
          // Skip the -1px reduction on the cornerBorderWidth when
          // selectedVariant === "1" (Sud Express opts out).
          skipCornerBorderWidthReduction: z.boolean().optional(),
          // ── Buy CTA bottom offset on desktop ──────────────────────
          // Push the main "buy" CTA up by N from the bottom on desktop
          // (Givenchy "195px"; default 0). Used to make room for the
          // pinned restart link below it.
          buyCTABottomDesktop: css,
          // ── Restart (skip) link layout ────────────────────────────
          // restart link height / max-height (Givenchy 40/38, TDF
          // 28/28, VB 30/default).
          restartCTAHeight: css,
          restartCTAMaxHeight: css,
          // Force padding 0 on the restart link (Givenchy, TDF).
          restartCTAPaddingZero: z.boolean().optional(),
          // Pin the restart link to a fixed bottom offset on desktop
          // when NOT showing similar products (Givenchy desktop only).
          restartCTAPinDesktop: z.boolean().optional(),
          restartCTAPinBottomDesktop: css,
          restartCTAPinWidthDesktop: css,
          // Margin-bottom on the restart link when showing similar
          // products on desktop (Givenchy "50px").
          restartCTAMarginBottomDesktopShowSimilar: css,
          // Restart (skip) link background-color override (SRP
          // sets "transparent"; default keeps restartCTA.backgroundColor
          // from the styling config).
          skipCTABackgroundColor: css,
          // Restart link font-size override on mobile (SRP "12px").
          skipFontSizeMobile: css,
          // ── Skip CTA top margin (the "restart" / skip link below) ──
          // Top margin on the skip link when in similar-products mode
          // on desktop (Givenchy desktop "40px"; default falls through
          // to legacy 20px / mobile 0).
          skipMarginTopDesktopShowSimilar: css,
          // Top margin on the skip link on desktop generally (Soeur
          // "20px"; Gerard Darel desktop "10px").
          skipMarginTopDesktop: css,
          // ── Container paddingBottom ───────────────────────────────
          // Apply paddingBottom on the outer container when showing
          // similar products on desktop (Givenchy desktop "40px";
          // default 0).
          containerPaddingBottomDesktopShowSimilar: css,
          // ── Misc ──────────────────────────────────────────────────
          // Render a ChevronRight on the main "buy" CTA (Intersport).
          showChevronOnCTA: z.boolean().optional(),
          // Render the Lacoste-style info box with size-parameter
          // copy (Lacoste).
          showSizeParameterBox: z.boolean().optional(),
          // Render a client-specific size guide footer block at the
          // bottom of the result. The component is currently the
          // SizeGuideFooter sub-component (Sessun); the flag itself
          // is generic so any client can wire in their own footer.
          showSizeGuideFooter: z.boolean().optional(),
          // Render the example_tailes / example_tailes_2 image at the
          // bottom (Soeur → "example_tailes", Weill → "example_tailes_2";
          // null = no example image).
          exampleSizesImage: z.enum(["example_tailes", "example_tailes_2"]).optional(),
          // Hide the MeasuredBy footer (Lacoste opts out — uses its
          // own size-parameter box in place of the footer).
          hideMeasuredBy: z.boolean().optional(),
          // Use selectedSize.label (instead of variant_id) when
          // posting the selected size to the parent frame (Antonelle).
          postSelectByLabel: z.boolean().optional(),
          // Accept the similar-products response even when its request
          // id is stale (i.e. a newer findSimilarProducts call has
          // started in the meantime). Some retailer integrations
          // dedupe on the client side instead of the request side, so
          // accepting stale-id responses avoids dropping valid data.
          acceptStaleSimilarProductsResponse: z.boolean().optional(),
          // Drop the variant_id when calling findSimilarProducts —
          // these retailers don't have a stable variant_id and the
          // similar-products endpoint must be called with only the
          // product id (Place des Tendances, Rodier, La Canadienne).
          noVariantIdOnSimilarProducts: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the UnavailableGallery component (carousel of
      // similar products on the unavailable-result screen).
      unavailableGallery: z
        .object({
          // Card layout variant. Default: a single-image card with
          // title + price below. "imageOverlay" = a bordered image
          // with the title + price stacked in a compact info row
          // underneath (Yuxus uses this variant).
          cardVariant: z.enum(["default", "imageOverlay"]).optional(),
          // Outer container gap between arrows and the carousel
          // (Givenchy "20px"; default "10px").
          containerGap: css,
          // Gap between the image and the text inside each card
          // (Givenchy "5px"; default "15px").
          itemGap: css,
          // Arrow button size (Givenchy "40px"; default "24px").
          arrowSize: css,
          // Arrow button margin-bottom (Givenchy "80px" lines it up
          // with their card height; default "40px").
          arrowMarginBottom: css,
          // Arrow button background color (Givenchy "transparent";
          // default "#fff").
          arrowBackgroundColor: css,
          // Use a chevron-style arrow icon (ArrowIconRight, rotated
          // for the left arrow) instead of the default ArrowIcon.
          useChevronArrows: z.boolean().optional(),
          // Force the carousel container width to 100% on desktop
          // (Givenchy desktop).
          containerWidthFullDesktop: z.boolean().optional(),
          // Always-zero title margin (Givenchy keeps a flat "0px"
          // around the gallery title).
          titleMarginAlwaysZero: z.boolean().optional(),
          // Zero title margin specifically on desktop (Gerard Darel
          // desktop only).
          titleMarginZeroDesktop: z.boolean().optional(),
          // Image width overrides per device. Default desktop "273px"
          // / mobile "250px" / iOS-mobile "180px".
          imageWidthDesktop: css,
          imageWidthMobile: css,
          imageWidthIOSMobile: css,
          // Price layout: "amount-first" (default, e.g. "30€"),
          // "currency-first" (Givenchy, e.g. "€30"), or "ca-suffix"
          // (Galore in the CA market, e.g. "$30 CAD" with the en-CA
          // currency formatter).
          priceFormat: z.enum(["amount-first", "currency-first", "ca-suffix"]).optional(),
          // Apply the CA-suffix price format only when the URL ?market
          // (or countryCode) param matches "CA" (Galore opts in).
          ifMarketCAUsePriceFormatCASuffix: z.boolean().optional(),
          // Card item text alignment (Givenchy + Sans Complexe
          // "center"; default "left").
          itemTextAlign: z.enum(["left", "center"]).optional(),
          // Color override on the price text only (Sans Complexe
          // "#888888"; default keeps productPrice.fontColor).
          unavailablePriceColor: css,
          // Strip the trailing SKU-code suffix from the product title
          // (e.g. "Blouse fluide CROQUET/12076/006" → "Blouse fluide")
          // and sentence-case the result. Used when a retailer's
          // product titles embed model codes after the human label.
          titleStripCodeSuffix: z.boolean().optional(),
          // Capitalize only the first letter of the title (Givenchy).
          capitalizeFirstTitle: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the ShoeResult component (also used for the
      // generic result screen since this component is shared).
      shoeResult: z
        .object({
          // When the recommended size is unavailable, use the standard
          // recommendedSize color instead of the unavailable_size_text
          // color (SRP, Sans Complexe).
          unavailableUseRecommendedColor: z.boolean().optional(),
          // Override the "size title" font size on the apparel branch
          // (Simone Pérèle "14px"; default uses subtitles.fontSize).
          sizeTitleFontSize: css,
          // Margin around the description block (SRP "32px 0"; default
          // "15px 0").
          descriptionMargin: css,
          // Description font size (SRP "14px"; default uses
          // resultScreenUI.Description.fontSize).
          descriptionFontSize: css,
          // Render a ChevronRight icon on the main CTA (Intersport).
          showChevronOnCTA: z.boolean().optional(),
          // restart CTA layout overrides.
          restartCTA: z
            .object({
              maxHeight: css, // TDF "28px", Givenchy "40px"; default fit-content
              padding: css, // TDF "0", Peserico "12px 16px", VB "7px"; default "16px"
              height: css, // TDF "28px"; default depends on borderWidth
            })
            .optional(),
        })
        .optional(),
      // Overrides for the main popup container / header / titles /
      // description styles computed by hooks/usePopupStyles. Everything
      // is optional — defaults preserve the legacy non-brand-keyed
      // behavior. See the hook for resolution order.
      popup: z
        .object({
          // ── Width ─────────────────────────────────────────────────
          // Mobile popup width (Givenchy mobile "calc(100% - 40px)";
          // default "calc(100% - 50px)").
          widthMobile: css,
          // Desktop popup width (Givenchy "50%", Simone Pérèle /
          // compactInputGroup brands / Sessun "495px"; if undefined,
          // falls back to common.ui.all.desktopPopupWidth then "445px").
          widthDesktop: css,
          // ── Box ───────────────────────────────────────────────────
          // Background color (SRP "#F7F7F7"; default "#FFFFFF").
          backgroundColor: css,
          // Skip the default box-shadow (Givenchy).
          noBoxShadow: z.boolean().optional(),
          // Mobile border-radius (VB "10px 10px 0 0"; default 0).
          borderRadiusMobile: css,
          // Desktop border-radius (SRP "8px 0 0 8px"; default 0).
          borderRadiusDesktop: css,
          // Desktop box-sizing override (Givenchy "border-box").
          boxSizingDesktop: css,
          // ── Padding ───────────────────────────────────────────────
          // Per-device padding rules. Default = { top:"25px",
          // bottom:"10px", horizontal:"25px" }.
          paddingMobile: z
            .object({ top: css, bottom: css, horizontal: css })
            .optional(),
          paddingDesktop: z
            .object({ top: css, bottom: css, horizontal: css })
            .optional(),
          // ── Header ────────────────────────────────────────────────
          // Top margin on the header on mobile (SRP "20px"; default
          // "10px"; desktop always 0).
          headerMarginTopMobile: css,
          // KENZO opts out of the mobile-top margin on CONSENT step
          // (header marginTop = 0 instead of "10px").
          headerSkipMobileMarginTopOnConsent: z.boolean().optional(),
          // Force header marginBottom to 0 regardless of step
          // (Givenchy collapses the header).
          headerMarginBottomZero: z.boolean().optional(),
          // Force header marginBottom to a fixed value regardless of
          // step (TDF "0px").
          headerMarginBottomFixed: css,
          // Header width / right / bottom / background (SRP frames the
          // header differently with a white strip).
          headerWidth: css,
          headerPosition: css,
          headerRight: css,
          headerBottom: css,
          headerBackgroundColor: css,
          headerMinHeight: css,
          headerBorderRadiusDesktop: css,
          // ── Max height ────────────────────────────────────────────
          // Mobile max-height (SRP "" — unconstrained; default
          // "calc(100dvh - 80px)"). Desktop always "100%".
          maxHeightMobile: css,
          // ── Min height ────────────────────────────────────────────
          // Use "auto" min-height on mobile on ALL paths (iOS and
          // non-iOS). Givenchy mobile.
          minHeightAutoMobileAll: z.boolean().optional(),
          // Use "auto" min-height on mobile on the iOS path ONLY
          // (Etam, Maison 123, Sans Complexe).
          minHeightAutoMobileIOS: z.boolean().optional(),
          // Brand-wide mobile default (TDF "60dvh", Velour "60dvh";
          // default "52dvh"). Applies after per-step overrides miss.
          minHeightDefaultMobile: css,
          // Per-step minHeight overrides on the mobile non-iOS path,
          // keyed by step number. KENZO / VB / Velour set entries.
          minHeightStepOverrides: z.record(z.string(), css).optional(),
          // Mobile iOS per-step overrides (currently no brand uses
          // them — the iOS path keeps fixed defaults for GENDER and
          // UNAVAILABLE_RESULT).
          minHeightStepOverridesIOS: z.record(z.string(), css).optional(),
          // ── Title font-size adaptive reduction ────────────────────
          // Skip the auto title font-size reduction on adaptive
          // heights (Intersport, Blackstore, TDF, Givenchy).
          skipTitleFontReduction: z.boolean().optional(),
          // Skip the mobile "14px" fallback (Givenchy keeps its
          // configured size on mobile).
          skipMobileTitleFontFallback: z.boolean().optional(),
          // Reverse the height-relative breakpoint used by the
          // adaptive reductions: when true, the "medium height" query
          // triggers the reduction (Sporty & Rich's smaller layouts).
          useMediumHeightBreakpoint: z.boolean().optional(),
          // ── Description font-size adaptive reduction ──────────────
          // Skip the auto description font-size reduction (Intersport,
          // Blackstore, SRP, TDF, Givenchy, compactInputGroup brands).
          skipDescriptionFontReduction: z.boolean().optional(),
          // ── Description margins ───────────────────────────────────
          // Static description marginTop (SRP "40px", TDF "15px").
          // Overrides the default which is "10px" or "20px" adaptive.
          descriptionMarginTop: css,
          // Desktop-only description marginTop (Givenchy desktop
          // "10px"; default falls back to descriptionMarginTop).
          descriptionMarginTopDesktop: css,
          // Mobile description marginBottom (Givenchy mobile "10px",
          // TDF "20px"; default "30px" mobile).
          descriptionMarginBottomMobile: css,
          // Desktop description marginBottom (TDF "20px"; default
          // "40px" desktop).
          descriptionMarginBottomDesktop: css,
          // Tighten the description margins (top→0, bottom→"25px") on
          // both mobile + desktop, independent of the broader
          // `compactInputGroup` opt-in. Dev's `isCompactInputGroup`
          // predicate in usePopupStyles included Sans Complexe even
          // though its sibling `isEtamGroup` (TextField / CTA marginTop)
          // did not — this flag captures that narrower scope so Sans
          // Complexe can opt into the description spacing without
          // inheriting the full compactInputGroup field-layout side
          // effects. (Etam + Maison 123 set this too in addition to
          // compactInputGroup; Sans Complexe sets only this.)
          compactDescriptionSpacing: z.boolean().optional(),
        })
        .optional(),
      // Overrides shared between the 4 body-question steps
      // (StepTorse, StepSleeves, StepCuisses, StepBelly).
      stepBody: z
        .object({
          // Top margin on the description block (Givenchy "10px", TDF
          // "0px"; default keeps descriptionStyles.marginTop).
          descriptionMarginTop: css,
          // Bottom margin on the description block on mobile (Givenchy
          // mobile "40px"; default 0).
          descriptionMarginBottomMobile: css,
          // Top margin on the images / silhouette block on mobile
          // (default "20px"; TDF "5px").
          imagesMarginTopMobile: css,
          // Top margin on the images / silhouette block on desktop
          // (default "40px"; TDF "5px").
          imagesMarginTopDesktop: css,
          // Gap between silhouette items on mobile (default "10px";
          // TDF "0px").
          imagesGapMobile: css,
          // Gap between silhouette items on desktop (default "30px";
          // TDF "0px").
          imagesGapDesktop: css,
          // Hide the stepper component on these step pages (SRP, VB,
          // Givenchy, TDF skip the stepper to keep their layouts
          // cleaner).
          hideStepper: z.boolean().optional(),
          // Override the total step count shown by the stepper (Hast
          // injects an extra Sleeves step → 4 instead of the usual
          // M=3 / F=2 inferred from gender).
          stepperCountOverride: z.number().optional(),
          // Show a ChevronRight icon on the Continue CTA (Intersport).
          showChevronOnContinue: z.boolean().optional(),
          // String override for the Torso step's description text
          // (Victoria Beckham hard-codes "Stomach Shape" in English).
          torseLabelOverride: z.string().optional(),
          // String override for the Belly/Hip step (VB "Hip Shape").
          bellyLabelOverride: z.string().optional(),
          // Skip the capitalizeFirstLetter pass on the Continue CTA
          // text on these step pages (Theodore wants the raw lowercase
          // "continue" key).
          skipContinueCapitalize: z.boolean().optional(),
          // On the StepCuisses screen, use the torso translation file's
          // "continue" key instead of the cuisses one (Hast renames the
          // CTA on the cuisses screen).
          cuissesUseTorsoContinue: z.boolean().optional(),
          // Add this brand to the silhouettes-map lookup in StepTorse
          // / StepBelly (default uses only the brands returned by
          // bellyChoice.useFemaleSilhouetteMap; Givenchy opts in here).
          useSilhouettesMap: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the BellyChoice card component (the image+label
      // selection cards on the Belly / Torse / Cuisses / Sleeves
      // / Lingerie steps). Every primitive defaults to legacy.
      bellyChoice: z
        .object({
          // ── Card outer dimensions ──────────────────────────────────
          // Width override on mobile (Simone Pérèle 3-column dense
          // grid: "calc((100% - 16px) / 3)").
          cardWidthMobile: css,
          // Width override on desktop (Simone Pérèle "222px").
          cardWidthDesktop: css,
          // Height on mobile (Givenchy "155px"; Victoria Beckham +
          // Simone Pérèle "auto"; default "220px").
          cardHeightMobile: css,
          // Height on desktop (Givenchy "220px", Victoria Beckham
          // "auto", additional-brand presets "240px", Simone Pérèle
          // "auto"; default "220px").
          cardHeightDesktop: css,
          // Minimum height on mobile (Givenchy "155px", Simone Pérèle
          // "200px"; default "177px").
          cardMinHeightMobile: css,
          // Minimum height on desktop (Simone Pérèle "270px"; default
          // "177px").
          cardMinHeightDesktop: css,
          // Drop the border on the card (Victoria Beckham).
          cardRemoveBorder: z.boolean().optional(),
          // ── Card inner spacing ─────────────────────────────────────
          // Gap inside the card between image and label (Givenchy
          // "20px", Simone Pérèle "0px"; default loading-aware "10px").
          cardInnerGap: css,
          // Apply Simone Pérèle's mobile / desktop selection-padding
          // override.
          cardUseTightSelectionPadding: z.boolean().optional(),
          // ── Image inside card ──────────────────────────────────────
          // Width (Givenchy "auto", additional-brand presets "100%",
          // Simone Pérèle "auto"; default "90px").
          imageWidth: css,
          // Max-width (Givenchy "150px", additional-brand presets
          // "100%", Simone Pérèle "100%"; default "120px").
          imageMaxWidth: css,
          // Height on mobile (additional-brand presets "auto", Simone
          // Pérèle "110px"; default "120px").
          imageHeightMobile: css,
          // Height on desktop (additional-brand presets "auto",
          // Givenchy "170px", Simone Pérèle "210px"; default "120px").
          imageHeightDesktop: css,
          // Max-height on desktop applied only when not lingerie
          // (additional-brand presets "190px").
          imageMaxHeightDesktop: css,
          // CSS transform on mobile (Ogier "scale(1.4)").
          imageTransformMobile: css,
          // CSS transform on desktop (Ogier "scale(1.2)").
          imageTransformDesktop: css,
          // Margin-top on the image (Givenchy "15px", additional-brand
          // presets "0px"; default 0).
          imageMarginTop: css,
          // Drop the inherited border-radius on the image (additional-
          // brand presets render full-bleed images).
          imageRemoveBorderRadius: z.boolean().optional(),
          // CSS object-fit override (Givenchy "cover"; default
          // "initial" or "contain" when lingerie).
          imageObjectFitOverride: css,
          // ── Data container (image + text wrapper) ──────────────────
          // Gap between image and label inside the data container
          // (Givenchy 0, additional-brand presets "0px"; default "5px").
          dataContainerInnerGap: css,
          // ── Label / span styling ───────────────────────────────────
          // Top offset on mobile (Givenchy "30px", Ogier "20px";
          // default "10px").
          spanTopMobile: css,
          // Top offset on desktop (Givenchy "40px", Boggi Milano
          // "-15px"; default "10px").
          spanTopDesktop: css,
          // Mobile font-size override on the label (Boggi Milano
          // "11px").
          spanFontSizeMobile: css,
          // Horizontal margin override on the label (Boggi Milano
          // "0 8px").
          spanMargin: css,
          // Reduce the mobile font-size by 2px (The Kooples).
          reduceMobileFontSize: z.boolean().optional(),
          // Text-decoration applied when the card is selected
          // (Givenchy "underline").
          selectedTextDecoration: css,
          // Skip the default mobile min-height (2.6em) on the label
          // (Simone Pérèle).
          omitMobileMinHeight: z.boolean().optional(),
          // ── Layout ─────────────────────────────────────────────────
          // Render the label OUTSIDE the data container so it sits
          // below the card (Simone Pérèle).
          cardLayoutSplitText: z.boolean().optional(),
          // ── Container-level gap (between adjacent cards) ───────────
          // Gap between adjacent cards on mobile (Givenchy "20px", TDF
          // "16px", Victoria Beckham "0px"; default "8px").
          cardsGapMobile: css,
          // Gap between adjacent cards on desktop (Givenchy "20px",
          // TDF "16px"; default "8px").
          cardsGapDesktop: css,
          // ── Female silhouette map opt-in ───────────────────────────
          // When true, StepBelly + StepTorse / StepCuisses use the
          // female silhouette image map for this brand (legacy
          // handleAdditionalBrands flag — VB / Nina Ricci / Rabanne /
          // Ogier / Boggi Milano historically opted in).
          useFemaleSilhouetteMap: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the modal close button variant.
      closeButton: z
        .object({
          // Variant for the desktop close button. Default: standard
          // icon. "text" = render the literal "Close" word (Givenchy
          // desktop); "textWithIcon" = label + small close icon (TDF
          // desktop); "roundedHover" = black hover-animated icon
          // (LeCollet).
          variantDesktop: z.enum(["default", "text", "textWithIcon", "roundedHover"]).optional(),
          // Variant for the mobile close button. Default: standard
          // icon. "textWithIcon" = label + small icon (TDF mobile);
          // "roundedHover" = black hover-animated icon (LeCollet);
          // "customLargeSvgIcon" = a 40×40 client-specific SVG.
          variantMobile: z.enum(["default", "textWithIcon", "roundedHover", "customLargeSvgIcon"]).optional(),
          // Top offset (LeCollet "7px"; default "10px").
          topOffset: css,
          // Right offset on mobile for the "customLargeSvgIcon" variant
          // (Givenchy mobile "5px"; default "20px").
          iconMobileRight: css,
        })
        .optional(),
      // Overrides for the consent / cookie screen.
      consent: z
        .object({
          // Checkbox visual variant. "default" = the shared
          // CustomCheckbox (MUI Checkbox). "filled" = a filled square
          // checkbox with the checkmark inside (Givenchy).
          checkboxVariant: z.enum(["default", "filled"]).optional(),
          // Margin around the description paragraphs (Givenchy
          // "40px 0"; default 0).
          descriptionMargin: css,
          // Override text color on description + access label + links
          // (Givenchy "#000000"; default uses descriptionStyles.color).
          textColor: css,
          // Per-paragraph spacing (Givenchy mt 0 / mb 20px / line-height
          // 20px; default mt 10px / mb 15px).
          paragraphMarginTop: css,
          paragraphMarginBottom: css,
          paragraphLineHeight: css,
          // Align the checkbox + label by baseline instead of center
          // (Givenchy).
          checkboxAlignmentBaseline: z.boolean().optional(),
          // Push the access label down by Npx (Givenchy "10px").
          accessLabelBottomOffset: css,
          // Pin the lower button block to the bottom of the popup on
          // desktop (Givenchy desktop only — toggled off when the
          // viewport is small-height).
          pinBottomDesktop: z.boolean().optional(),
          pinBottomDesktopOffset: css,
          pinBottomDesktopWidth: css,
          // CTA tweaks (Givenchy / TDF tighter heights, Givenchy mobile
          // 30px top margin etc.).
          ctaMarginTopDesktop: css,
          ctaMarginTopMobile: css,
          ctaMaxHeight: css,
          ctaMarginBottomDesktopSmallHeight: css,
          // Show the "Measured by Kleep" footer (Givenchy keeps it on
          // the consent step; default hides it).
          showMeasuredBy: z.boolean().optional(),
          // Render a ChevronRight on the submit CTA (Intersport).
          showChevronOnSubmit: z.boolean().optional(),
        })
        .optional(),
      // Overrides for the result-error screen (out-of-range / scan
      // failure). All fields are optional; defaults match the legacy
      // non-brand-keyed behavior.
      resultError: z
        .object({
          // Use the default error_description key regardless of category
          // (TDF: always show the standard message, even on footwear).
          forceDefaultMessage: z.boolean().optional(),
          // Use a custom "shoeMessage_outOfRange" translation key on
          // footwear + out-of-range (Intersport, Blackstore).
          useCustomShoeOutOfRange: z.boolean().optional(),
          // Skip the capitalize() pass on the lingerie + out-of-range
          // path (Simone Pérèle keeps the raw translated string).
          skipCapitalizeLingerieOutOfRange: z.boolean().optional(),
          // Hide the entire description block when out-of-range
          // (Loom suppresses the error copy entirely).
          hideOutOfRangeMessage: z.boolean().optional(),
          // Skip the subtitle capitalization branch on errors (Givenchy
          // and Project X Paris keep their own case).
          skipCapitalizeSubtitle: z.boolean().optional(),
          // Force textTransform: "none" on the error description
          // (Givenchy, PXP, TDF — preserves brand copy casing).
          skipTextTransform: z.boolean().optional(),
          // Use display: "block" on the error description (TDF, PXP).
          displayBlock: z.boolean().optional(),
          // Top margin on the description on desktop when no compact
          // input group is active (SRP "60px"; default falls back to
          // the existing large-height / mobile-aware default).
          marginTopDesktop: css,
          // Render a "store locator" secondary CTA above the main skip
          // CTA (Givenchy).
          showStoreLocatorCTA: z.boolean().optional(),
          // Offset (bottom) for the store-locator CTA on desktop
          // (Givenchy desktop "200px"; default 0).
          storeLocatorBottomDesktop: css,
          // Force the skip CTA to use uppercase text (Givenchy).
          ctaUppercase: z.boolean().optional(),
          // Force the skip CTA to a transparent background +
          // transparent border + #000 text (Givenchy).
          ctaTransparent: z.boolean().optional(),
          // Disable the hover effect on the skip CTA (Givenchy keeps
          // a static look).
          disableCTAHover: z.boolean().optional(),
          // Show a small ChevronRight icon next to the skip CTA label
          // (Intersport).
          showChevronOnSkip: z.boolean().optional(),
        })
        .optional(),
      // Per-brand overrides applied on top of cta.* for the Continue
      // button that appears on body-measurement steps (Belly, Torse,
      // Sleeves, Cuisses). When a field is omitted the default fallback
      // is used.
      continueButton: z
        .object({
          // Mobile margin-top when an option is selected (Givenchy: 60px,
          // default 20px).
          marginTopMobileSelected: css,
          // Padding override (TDF "0", Peserico "12px 16px", VB "7px",
          // default "16px").
          padding: css,
          // Height override (TDF "28px", VB "30px", default "auto").
          height: css,
          // Max-height override (TDF "28px", Givenchy "40px",
          // default "fit-content").
          maxHeight: css,
          // Desktop-only position override (Givenchy: "fixed",
          // default "relative").
          positionDesktop: css,
          // Desktop-only bottom offset (Givenchy: "150px", default "0").
          bottomDesktop: css,
          // Desktop-only width override (Givenchy: "calc(100% - 160px)",
          // default "").
          widthDesktop: css,
        })
        .optional(),
    }),
  }),
  clothing: z.object({
    ux: clothingUXConfig,
    ui: clothingUI,
  }),
  lingerie: z.object({
    ux: flowUXConfig,
    ui: z.object({
      braSize: femaleBraScreenUI,
      questionnaire: lingerieQuestionnaireUI,
    }),
  }),
  shoe: z
    .object({
      ux: shoeUXConfig,
      ui: z.object({}).passthrough(),
    })
    .optional(),
  children: z
    .object({
      ux: flowUXConfig,
      ui: z.object({}).passthrough(),
    })
    .optional(),
});

// ─── Inferred types (single source of truth) ───────────────────────────────

export type BrandConfig = z.infer<typeof brandConfigSchema>;
export type RouteType = z.infer<typeof routeType>;
export type PrivacyPolicyConfig = z.infer<typeof privacyPolicyConfig>;
